import { isEmpty, sortByKey } from '@semios/app-platform-common'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { SearchTextInput } from 'components/SearchTextInput/SearchTextInput'
import { translate } from 'i18n/i18n'
import { useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { searchStringFromStringWithSpaces } from 'utils/searchStringFromStringWithSpaces'
import type { TEmitterConfiguration, TPropertyZoneData, TZoneFlowRateByEmitterType } from '../types'

// The matcher splits on spaces and ignores empty terms, so whitespace-only text filters nothing.
const hasSearchTerms = (text: string) => text.trim() !== ''

export type TListedProperty = Omit<TPropertyZoneData, 'irrigationZones'> & {
  irrigationZones: TZoneFlowRateByEmitterType[]
}

export type TZonesFilterChange = {
  // True when the user emptied a filter that was in use
  cleared: boolean
  // What the list will show once the change is applied.
  nextList: TListedProperty[]
}

type TZonesFilter = {
  emitterConfigurations: TEmitterConfiguration
  onFilterChange?: (change: TZonesFilterChange) => void
}

export const useZonesFilter = ({ emitterConfigurations, onFilterChange }: TZonesFilter) => {
  const [filterZoneText, setfilterZoneText] = useState('')
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([])
  const propertiesFromFass = fieldAssetStore.useSelector((s) => s.properties)
  const initialProperties = useMemo(() => Object.values(emitterConfigurations), [emitterConfigurations])

  // Memoised because the MultiSelect resets its search text
  // whenever it receives a new `data` array.
  const propertyOptions = useMemo(
    () =>
      initialProperties
        .map((p) => ({
          value: String(p.propertyId),
          label: propertiesFromFass?.[p.propertyId]?.propertyName ?? '',
          group: propertiesFromFass?.[p.propertyId]?.organizationName,
        }))
        .sort(sortByKey('label')),
    [initialProperties, propertiesFromFass],
  )

  const groupPropertyOptions = new Set(propertyOptions.map((o) => o.group)).size > 1

  const getFilteredProperties = (propertyIds: string[], zoneText: string) => {
    let result = [...initialProperties]

    if (propertyIds.length > 0) {
      result = result.filter((p) => propertyIds.includes(String(p.propertyId)))
    }

    if (zoneText) {
      result = result.map((p) => {
        let filteredIrrigationZones = Object.fromEntries(
          Object.entries(p.irrigationZones).filter(([, val]) => {
            const isZoneName = searchStringFromStringWithSpaces(zoneText, val.zoneName)

            const isEmitterType = Object.values(val.flowRate).some((f) =>
              searchStringFromStringWithSpaces(zoneText, mapApiEmitterTypeToLabel(f.emitterType)),
            )

            return isZoneName || isEmitterType
          }),
        )

        filteredIrrigationZones = Object.fromEntries(
          Object.entries(filteredIrrigationZones).map(([key, val]) => {
            const filteredFlowRate = Object.fromEntries(
              Object.entries(val.flowRate).filter(([, flowRateVal]) =>
                searchStringFromStringWithSpaces(zoneText, mapApiEmitterTypeToLabel(flowRateVal.emitterType)),
              ),
            )

            const isZoneName = searchStringFromStringWithSpaces(zoneText, val.zoneName)

            return [
              key,
              {
                ...val,
                flowRate: isZoneName ? val.flowRate : filteredFlowRate,
              },
            ]
          }),
        )

        return {
          ...p,
          irrigationZones: filteredIrrigationZones,
        }
      })
    }

    return result.filter((p) => Object.values(p.irrigationZones).length > 0)
  }

  const getFilteredAndSortedList = (propertyIds: string[], zoneText: string): TListedProperty[] =>
    getFilteredProperties(propertyIds, zoneText)
      .map((p) => ({
        ...p,
        propertyName: propertiesFromFass?.[Number(p.propertyId)]?.propertyName ?? '',
        irrigationZones: Object.values(p.irrigationZones).sort(sortByKey('zoneName')),
      }))
      .sort(sortByKey('propertyName'))

  const filteredAndSortedList = getFilteredAndSortedList(selectedPropertyIds, filterZoneText)

  const changeSelectedPropertyIds = (nextPropertyIds: string[]) => {
    setSelectedPropertyIds(nextPropertyIds)

    onFilterChange?.({
      cleared: selectedPropertyIds.length > 0 && nextPropertyIds.length === 0,
      nextList: getFilteredAndSortedList(nextPropertyIds, filterZoneText),
    })
  }

  const changeFilterZoneText = (nextZoneText: string) => {
    setfilterZoneText(nextZoneText)

    onFilterChange?.({
      cleared: hasSearchTerms(filterZoneText) && !hasSearchTerms(nextZoneText),
      nextList: getFilteredAndSortedList(selectedPropertyIds, nextZoneText),
    })
  }

  return {
    filterComponent: (
      <div css={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div css={{ flexGrow: 2 }}>
          <SearchTextInput
            placeholder={translate.phrases.banyanApp('Filter zones or emitter types')}
            searchText={filterZoneText}
            setSearchText={changeFilterZoneText}
            clearable={true}
          />
        </div>
        <div css={{ flex: '1 1 280px' }}>
          <MultiSelect
            data={propertyOptions}
            selectedData={propertyOptions.filter((o) => selectedPropertyIds.includes(o.value))}
            onChange={(selected: TOptions[]) =>
              changeSelectedPropertyIds(selected.map((o) => String(o.value)))
            }
            placeholder={translate.phrases.banyanApp('Select Properties')}
            groupOptions={groupPropertyOptions}
            disabled={isEmpty(initialProperties)}
          />
        </div>
      </div>
    ),
    filteredAndSortedList,
    isSearchMatch: filteredAndSortedList.length > 0,
  }
}
